import React, { useMemo } from 'react';
import { Box, Typography, Paper, Grid, Divider, Card, CardContent } from '@mui/material';
import AircraftGauge from './AircraftGauge';
import { Business, Person, TrendingUp, Flight, Speed, Navigation } from '@mui/icons-material';

interface GaugesDashboardProps {
  categoryScores: Record<string, number>;
  language: string;
}

// Category name mappings with pilot-style descriptions
const categoryNames: Record<string, { hebrew: string; english: string; hebrewDesc: string; englishDesc: string }> = {
  'management': {
    hebrew: 'ניהול',
    english: 'Executive',
    hebrewDesc: 'מערכת ניווט ראשית - קביעת כיוון אסטרטגי',
    englishDesc: 'Primary Navigation System - Strategic Direction Control'
  },
  'organization': {
    hebrew: 'ארגון',
    english: 'Organization',
    hebrewDesc: 'מערכת ייצוב טיסה - איזון מבני ותפעולי',
    englishDesc: 'Flight Stabilization System - Structural Balance Control'
  },
  'repeatSales': {
    hebrew: 'מכירות חוזרות',
    english: 'Repeat Sales',
    hebrewDesc: 'מד דלק - שימור לקוחות ונאמנות',
    englishDesc: 'Fuel Gauge - Customer Retention & Loyalty'
  },
  'finance': {
    hebrew: 'כספים',
    english: 'Finance',
    hebrewDesc: 'מד גובה כלכלי - בקרת משאבים פיננסיים',
    englishDesc: 'Economic Altimeter - Financial Resource Control'
  },
  'operations': {
    hebrew: 'תפעול',
    english: 'Operations',
    hebrewDesc: 'מד מהירות - יעילות תהליכים תפעוליים',
    englishDesc: 'Airspeed Indicator - Operational Process Efficiency'
  },
  'qualityControl': {
    hebrew: 'בקרת איכות',
    english: 'Quality Control',
    hebrewDesc: 'מערכת אבחון - ניטור ובקרת איכות',
    englishDesc: 'Diagnostic System - Quality Monitoring & Control'
  },
  'newSales': {
    hebrew: 'מכירות חדשות',
    english: 'New Sales',
    hebrewDesc: 'רדאר עסקי - זיהוי הזדמנויות חדשות',
    englishDesc: 'Business Radar - New Opportunity Detection'
  },
  'stress': {
    hebrew: 'מתח',
    english: 'Stress',
    hebrewDesc: 'מד לחץ - ניהול עומס ומתח',
    englishDesc: 'Pressure Gauge - Load & Stress Management'
  },
  'principles': {
    hebrew: 'עקרונות בסיסיים',
    english: 'Principles',
    hebrewDesc: 'מצפן מוסרי - ערכי ליבה וכיוון',
    englishDesc: 'Moral Compass - Core Values & Direction'
  },
  'helpAcceptance': {
    hebrew: 'עזרה',
    english: 'Help',
    hebrewDesc: 'מערכת תקשורת - פתיחות לסיוע ושיתוף',
    englishDesc: 'Communication System - Openness to Support & Collaboration'
  },
};

const GaugesDashboard: React.FC<GaugesDashboardProps> = ({ categoryScores, language }) => {
  // Process category scores
  const processedScores = useMemo(() => {
    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        key: categoryKey,
        name: language === 'he'
          ? categoryNames[categoryKey]?.hebrew
          : categoryNames[categoryKey]?.english,
        description: language === 'he'
          ? categoryNames[categoryKey]?.hebrewDesc
          : categoryNames[categoryKey]?.englishDesc,
        value: score as number
      };
    });

    return {
      business: scores.slice(0, 7),  // First 7 categories
      personal: scores.slice(7, 10),  // Last 3 categories
      businessAverage: scores.slice(0, 7).reduce((sum, cat) => sum + cat.value, 0) / 7,
      personalAverage: scores.slice(7, 10).reduce((sum, cat) => sum + cat.value, 0) / 3,
      overallAverage: scores.reduce((sum, cat) => sum + cat.value, 0) / scores.length
    };
  }, [categoryScores, language]);

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#27ae60';
    if (score >= 60) return '#2ecc71';
    if (score >= 40) return '#f39c12';
    return '#e74c3c';
  };

  const getScoreLabel = (score: number) => {
    if (language === 'he') {
      if (score >= 80) return 'מעולה';
      if (score >= 60) return 'טוב';
      if (score >= 40) return 'בינוני';
      return 'דורש שיפור';
    } else {
      if (score >= 80) return 'Excellent';
      if (score >= 60) return 'Good';
      if (score >= 40) return 'Average';
      return 'Needs Improvement';
    }
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Consolidated Dashboard - 2 Cards Only */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Business Cockpit Panel - Complete Card */}
        <Grid item xs={12} lg={6}>
          <Card elevation={3} sx={{
            height: '100%',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <Box sx={{
              p: 2,
              background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
              borderBottom: '3px solid #3498db'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Speed sx={{ color: 'white', fontSize: 28 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
                    {language === 'he' ? 'פאנל ניווט עסקי' : 'Business Navigation Panel'}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontStyle: 'italic' }}>
                  {language === 'he' ? 'מצב תפעולי' : 'Operational Status'}
                </Typography>
              </Box>
            </Box>

            <CardContent sx={{ p: 3 }}>
              {/* Main Cluster Gauge */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <AircraftGauge
                  value={processedScores.businessAverage}
                  label=""
                  size={200}
                />
              </Box>

              {/* Individual Category Gauges */}
              <Grid container spacing={2}>
                {processedScores.business.map((category) => (
                  <Grid item xs={12} sm={6} md={4} key={category.key}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      p: 2,
                      minHeight: '220px',
                      borderRadius: 2,
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                      border: '1px solid rgba(52,73,94,0.1)',
                      position: 'relative',
                      overflow: 'visible',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: `linear-gradient(90deg, ${getScoreColor(category.value)} 0%, ${getScoreColor(category.value)}88 100%)`,
                      }
                    }}>
                      <AircraftGauge
                        value={category.value}
                        label={category.name || ''}
                        size={110}
                        showDescription={true}
                        description={category.description}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Personal Control Panel - Complete Card */}
        <Grid item xs={12} lg={6}>
          <Card elevation={3} sx={{
            height: '100%',
            background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <Box sx={{
              p: 2,
              background: 'linear-gradient(135deg, #d35400 0%, #e67e22 100%)',
              borderBottom: '3px solid #f39c12'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person sx={{ color: 'white', fontSize: 28 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
                    {language === 'he' ? 'פאנל בקרה אישי' : 'Personal Control Panel'}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontStyle: 'italic' }}>
                  {language === 'he' ? 'מצב אישי' : 'Personal Status'}
                </Typography>
              </Box>
            </Box>

            <CardContent sx={{ p: 3 }}>
              {/* Main Cluster Gauge */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <AircraftGauge
                  value={processedScores.personalAverage}
                  label=""
                  size={200}
                />
              </Box>

              {/* Individual Category Gauges */}
              <Grid container spacing={2} justifyContent="center">
                {processedScores.personal.map((category) => (
                  <Grid item xs={12} sm={6} md={4} key={category.key}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      p: 2,
                      minHeight: '220px',
                      borderRadius: 2,
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                      border: '1px solid rgba(230,126,34,0.2)',
                      position: 'relative',
                      overflow: 'visible',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: `linear-gradient(90deg, ${getScoreColor(category.value)} 0%, ${getScoreColor(category.value)}88 100%)`,
                      }
                    }}>
                      <AircraftGauge
                        value={category.value}
                        label={category.name || ''}
                        size={110}
                        showDescription={true}
                        description={category.description}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    </Box>
  );
};

export default GaugesDashboard;